import { useRef, useState, useEffect } from "react";
import { motion, useScroll, AnimatePresence } from "framer-motion";
import { Phone, Inbox, Plug, Zap, AlertTriangle, Calendar, Check, MessageSquare, User, ChevronRight, Eye, LayoutDashboard, History, BarChart3, LifeBuoy, Settings, Star, Headphones, FileText, Play } from "lucide-react";
import Image from "@/components/ui/image";
import googleCalendarLogo from "@/assets/google-calendar-logo.png";
import jobberLogo from "@/assets/jobber-logo.png";
import opentableLogo from "@/assets/opentable-logo.png";
import housecallProLogo from "@/assets/housecall-pro-logo.webp";
import mindbodyLogo from "@/assets/mindbody-logo.png";
import hubspotLogo from "@/assets/hubspot-logo.png";
import ghlLogo from "@/assets/ghl-logo.png";

const features = [
  {
    id: "voice",
    icon: Phone,
    title: "Never miss a call.\nNever lose a lead.",
    subtitle: "Your AI receptionist answers instantly—day or night.\nQualifies leads. Books appointments. Handles FAQs.\nNo hold music. No voicemail. No missed opportunities.",
  },
  {
    id: "omnichannel",
    icon: Inbox,
    title: "Voice. Text. Email. DMs.\nOne inbox. All handled.",
    subtitle: "Customers don't pick one channel. They call. Text. DM. Email.\nAyra handles all of it. One unified inbox.\nInstant responses. Automatic bookings.",
  },
  {
    id: "visibility",
    icon: Eye,
    title: "Every call. Every detail.\nOne dashboard.",
    subtitle: "Call recordings. Transcripts. Analytics.\nComplete visibility into every conversation—without digging through tools.",
  },
  {
    id: "integrations",
    icon: Plug,
    title: "Works with the tools\nyou already use.",
    subtitle: "Your calendar. Your CRM. Your booking software.",
  },
  {
    id: "workflows",
    icon: Zap,
    title: "The call ends.\nAyra keeps working.",
    subtitle: "Confirmation sent. Reminder scheduled. Review requested.\nAll automatic. No manual follow-up. No dropped balls.\nYour business runs itself.",
  },
  {
    id: "escalations",
    icon: AlertTriangle,
    title: "When it's urgent?\nYou're always in the loop.",
    subtitle: "Complex requests get flagged instantly. You're never out of control.",
  },
];

// Voice AI Visual (Dark Mode) - Immersive waveform experience
const VoiceAIVisual = () => {
  const transcriptText = "I have 2pm and 4pm available on Saturday. Which works better for you?";
  const confirmText = "Perfect! I've booked you for Saturday at 2pm. You'll receive a confirmation text shortly.";
  
  // Initialize with text already started to avoid delay
  const [phase, setPhase] = useState<'speaking1' | 'listening' | 'speaking2' | 'confirmed' | 'hold'>('speaking1');
  const [timer, setTimer] = useState(0);
  const [displayedText, setDisplayedText] = useState(transcriptText.slice(0, 1));
  
  // Timer effect
  useEffect(() => {
    const interval = setInterval(() => {
      setTimer(prev => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Phase cycling
  useEffect(() => {
    const durations = { speaking1: 4000, listening: 2000, speaking2: 4500, confirmed: 3000, hold: 2000 };
    const nextPhase = { speaking1: 'listening', listening: 'speaking2', speaking2: 'confirmed', confirmed: 'hold', hold: 'speaking1' } as const;
    
    const timeout = setTimeout(() => {
      const next = nextPhase[phase];
      setPhase(next);
      if (next === 'speaking1') {
        setTimer(0);
        setDisplayedText('');
      }
    }, durations[phase]);
    
    return () => clearTimeout(timeout);
  }, [phase]);

  // Typing effect for transcription
  useEffect(() => {
    if (phase === 'speaking1') {
      const text = transcriptText;
      let index = 0;
      setDisplayedText('');
      const interval = setInterval(() => {
        if (index < text.length) {
          setDisplayedText(text.slice(0, index + 1));
          index++;
        } else {
          clearInterval(interval);
        }
      }, 50);
      return () => clearInterval(interval);
    } else if (phase === 'speaking2') {
      const text = confirmText;
      let index = 0;
      setDisplayedText('');
      const interval = setInterval(() => {
        if (index < text.length) {
          setDisplayedText(text.slice(0, index + 1));
          index++;
        } else {
          clearInterval(interval);
        }
      }, 45);
      return () => clearInterval(interval);
    }
  }, [phase]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const isSpeaking = phase === 'speaking1' || phase === 'speaking2';

  // Generate waveform bars
  const waveformBars = 32;

  const isConfirmed = phase === 'confirmed' || phase === 'hold';

  return (
    <div className="relative w-full h-full flex items-center justify-center p-4">
      {/* Ambient glow - blue tinted */}
      <motion.div 
        className="absolute inset-0"
        animate={{
          opacity: isSpeaking ? 0.5 : 0.25,
          background: 'radial-gradient(ellipse 80% 60% at 50% 50%, rgba(59, 130, 246, 0.2) 0%, transparent 70%)'
        }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      />
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-[400px] bg-dark-surface rounded-2xl border border-dark-border overflow-hidden"
        style={{ boxShadow: '0 0 60px rgba(0,0,0,0.5), 0 0 100px rgba(59, 130, 246, 0.08)' }}
      >
        {/* Header - Live Call indicator */}
        <div className="px-5 py-4 border-b border-dark-border bg-[hsl(216_28%_9%)]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-10 h-10 rounded-full bg-blue-500/15 flex items-center justify-center">
                  <Phone className="w-5 h-5 text-blue-400" />
                </div>
                <motion.div
                  className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-green-500"
                  animate={{ scale: [1, 1.15, 1], opacity: [1, 0.85, 1] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                />
              </div>
              <div>
                <div className="text-sm font-semibold text-dark-text">Live Call</div>
                <div className="text-[11px] text-dark-muted">AI Receptionist Active</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-500/15 border border-green-500/30">
                <motion.div 
                  className="w-1.5 h-1.5 rounded-full bg-green-500"
                  animate={{ opacity: [0.7, 1, 0.7] }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                />
                <span className="text-[10px] font-medium text-green-400">{formatTime(timer)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Caller Info */}
        <div className="px-5 py-3 border-b border-dark-border/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-dark-muted/30 flex items-center justify-center">
                <User className="w-4 h-4 text-dark-muted" />
              </div>
              <div>
                <div className="text-sm font-medium text-dark-text">Sarah Johnson</div>
                <div className="text-[10px] text-dark-muted">+1 (555) •••-••47</div>
              </div>
            </div>
            {/* Inline confirmation indicator */}
            <AnimatePresence>
              {isConfirmed && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8, x: 10 }}
                  animate={{ opacity: 1, scale: 1, x: 0 }}
                  exit={{ opacity: 0, scale: 0.8, x: 10 }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                  className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-green-500/15 border border-green-500/30"
                >
                  <Check className="w-3 h-3 text-green-400" />
                  <span className="text-[10px] font-medium text-green-400">Booked</span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Waveform Visualization - Blue, smoother */}
        <div className="px-5 py-6">
          <div className="flex items-center justify-center gap-[3px] h-16">
            {[...Array(waveformBars)].map((_, i) => {
              const baseHeight = Math.sin((i / waveformBars) * Math.PI) * 0.7 + 0.3;
              const phaseOffset = (i / waveformBars) * Math.PI * 2;
              return (
                <motion.div
                  key={i}
                  className="w-[6px] rounded-full"
                  style={{ backgroundColor: 'rgb(59, 130, 246)' }}
                  animate={{
                    height: isSpeaking 
                      ? [
                          `${baseHeight * 25 + Math.sin(phaseOffset) * 15}px`,
                          `${baseHeight * 45 + Math.cos(phaseOffset) * 10}px`,
                          `${baseHeight * 20 + Math.sin(phaseOffset + 1) * 18}px`,
                          `${baseHeight * 40 + Math.cos(phaseOffset + 1) * 12}px`,
                        ]
                      : `${baseHeight * 8}px`,
                    opacity: isSpeaking ? 0.9 : 0.25,
                  }}
                  transition={{
                    duration: 0.4,
                    repeat: isSpeaking ? Infinity : 0,
                    repeatType: "mirror",
                    ease: [0.4, 0, 0.2, 1],
                    delay: i * 0.015,
                  }}
                />
              );
            })}
          </div>
        </div>

        {/* Status indicator - blue themed */}
        <div className="px-5 pb-3">
          <div className="flex items-center justify-center gap-2">
            <motion.div
              animate={{ 
                scale: isSpeaking ? [1, 1.15, 1] : 1, 
                opacity: isSpeaking ? 1 : 0.5 
              }}
              transition={{ duration: 0.8, repeat: isSpeaking ? Infinity : 0, ease: "easeInOut" }}
              className="w-2 h-2 rounded-full bg-blue-400"
            />
            <motion.span 
              className="text-xs font-medium text-blue-400"
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              {phase === 'listening' ? 'Listening...' : isConfirmed ? 'Confirmed' : 'AI Speaking'}
            </motion.span>
          </div>
        </div>

        {/* Live Transcription - fixed height */}
        <div className="px-5 pb-5">
          <div className="bg-dark-muted/10 rounded-xl p-4 border border-dark-border/50 h-[100px] overflow-hidden">
            <AnimatePresence mode="wait">
              {(phase === 'speaking1' || phase === 'listening') && (
                <motion.div
                  key="transcript1"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.35, ease: "easeOut" }}
                >
                  <p className="text-sm text-dark-text leading-relaxed">
                    "{displayedText}"
                    {phase === 'speaking1' && displayedText.length < transcriptText.length && (
                      <motion.span
                        animate={{ opacity: [1, 0] }}
                        transition={{ duration: 0.5, repeat: Infinity, ease: "easeInOut" }}
                        className="inline-block w-0.5 h-4 bg-blue-400 ml-0.5 align-middle"
                      />
                    )}
                  </p>
                </motion.div>
              )}
              {(phase === 'speaking2' || isConfirmed) && (
                <motion.div
                  key="transcript2"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.35, ease: "easeOut" }}
                >
                  <p className="text-sm text-dark-text leading-relaxed">
                    "{displayedText}"
                    {phase === 'speaking2' && displayedText.length < confirmText.length && (
                      <motion.span
                        animate={{ opacity: [1, 0] }}
                        transition={{ duration: 0.5, repeat: Infinity, ease: "easeInOut" }}
                        className="inline-block w-0.5 h-4 bg-blue-400 ml-0.5 align-middle"
                      />
                    )}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Omnichannel Visual (Dark Mode) - Copied from Agency page
const OmnichannelVisual = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const posScale = isMobile ? 0.55 : 1;
  const bubblePadding = isMobile ? "px-3 py-2.5" : "px-4 py-3";

  return (
    <div className="relative w-full h-full flex items-center justify-center overflow-hidden max-w-[500px] mx-auto scale-[0.98] sm:scale-100">
      {/* Ambient background glow */}
      <div 
        className="absolute inset-0 opacity-30"
        style={{
          background: 'radial-gradient(ellipse 80% 60% at 50% 50%, hsl(var(--primary) / 0.15) 0%, transparent 70%)'
        }}
      />
      
      {/* Floating channel signals - distributed around the visual */}
      {[
        { icon: Phone, label: "Voice", x: -180, y: -100, delay: 0, color: "blue" },
        { icon: MessageSquare, label: "SMS", x: 160, y: -80, delay: 0.15, color: "green" },
        { icon: Inbox, label: "Email", x: -150, y: 80, delay: 0.25, color: "amber" },
        { icon: MessageSquare, label: "Chat", x: 180, y: 60, delay: 0.35, color: "purple" },
        { icon: MessageSquare, label: "DMs", x: -60, y: -140, delay: 0.1, color: "pink" },
      ].map((channel, i) => (
        <motion.div
          key={channel.label}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ 
            opacity: [0, 0.9, 0.7],
            scale: [0.8, 1, 0.95],
            x: [channel.x * 1.2 * posScale, channel.x * posScale, channel.x * 0.3 * posScale],
            y: [channel.y * 1.2 * posScale, channel.y * posScale, channel.y * 0.3 * posScale],
          }}
          transition={{ 
            duration: 4,
            delay: channel.delay,
            repeat: Infinity,
            repeatType: "reverse",
            ease: "easeInOut"
          }}
          className="absolute z-10"
          style={{ left: '50%', top: '50%' }}
        >
          <div className={`
            ${bubblePadding} rounded-2xl backdrop-blur-sm
            bg-dark-surface/80 border border-dark-border/50
            shadow-lg
          `}
          style={{ 
            boxShadow: channel.color === "blue" ? '0 8px 32px rgba(59, 130, 246, 0.15)' :
                       channel.color === "green" ? '0 8px 32px rgba(34, 197, 94, 0.15)' :
                       channel.color === "amber" ? '0 8px 32px rgba(245, 158, 11, 0.15)' :
                       channel.color === "purple" ? '0 8px 32px rgba(168, 85, 247, 0.15)' :
                       '0 8px 32px rgba(236, 72, 153, 0.15)'
          }}
          >
            <div className="flex items-center gap-2.5">
              <div className={`
                w-8 h-8 rounded-lg flex items-center justify-center
                ${channel.color === "blue" ? "bg-blue-500/15" :
                  channel.color === "green" ? "bg-green-500/15" :
                  channel.color === "amber" ? "bg-amber-500/15" :
                  channel.color === "purple" ? "bg-purple-500/15" :
                  "bg-pink-500/15"}
              `}>
                <channel.icon className={`w-4 h-4
                  ${channel.color === "blue" ? "text-blue-400" :
                    channel.color === "green" ? "text-green-400" :
                    channel.color === "amber" ? "text-amber-400" :
                    channel.color === "purple" ? "text-purple-400" :
                    "text-pink-400"}
                `} />
              </div>
              <span className="text-sm font-medium text-dark-text">{channel.label}</span>
            </div>
          </div>
        </motion.div>
      ))}

      {/* Subtle particle trails converging */}
      {[...Array(8)].map((_, i) => (
        <motion.div
          key={`particle-${i}`}
          className="absolute w-1.5 h-1.5 rounded-full bg-primary/30"
          style={{ left: '50%', top: '50%' }}
          initial={{ 
            opacity: 0,
            x: (Math.cos(i * Math.PI / 4) * 200),
            y: (Math.sin(i * Math.PI / 4) * 150),
          }}
          animate={{ 
            opacity: [0, 0.6, 0],
            x: [Math.cos(i * Math.PI / 4) * 200, 0],
            y: [Math.sin(i * Math.PI / 4) * 150, 0],
          }}
          transition={{
            duration: 3,
            delay: i * 0.4,
            repeat: Infinity,
            ease: "easeIn"
          }}
        />
      ))}

      {/* Central unified inbox - the gravitational center */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, delay: 0.3 }}
        className="relative z-20"
      >
        {/* Soft glow ring */}
        <div 
          className="absolute -inset-8 rounded-3xl opacity-40"
          style={{
            background: 'radial-gradient(ellipse at center, hsl(var(--primary) / 0.2) 0%, transparent 70%)'
          }}
        />
        
        {/* Main inbox card */}
        <div 
          className="relative bg-dark-surface rounded-2xl border border-dark-border"
          style={{ 
            width: isMobile ? '260px' : '300px',
            boxShadow: '0 0 60px rgba(95, 143, 132, 0.1), 0 25px 50px rgba(0,0,0,0.4)' 
          }}
        >
          {/* Header */}
          <div className="px-5 py-4 border-b border-dark-border bg-[hsl(216_28%_9%)] rounded-t-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-primary/15 flex items-center justify-center">
                  <Inbox className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-dark-text">Unified Inbox</div>
                  <div className="text-[10px] text-dark-muted">All channels • Real-time</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="w-2 h-2 rounded-full bg-primary"
                />
              </div>
            </div>
          </div>

          {/* Message list */}
          <div className="p-3 space-y-2">
            {[
              { type: "voice", name: "Sarah M.", preview: "Voicemail received", time: "Just now", unread: true },
              { type: "sms", name: "John D.", preview: "Thanks for the reminder!", time: "2m ago", unread: true },
              { type: "email", name: "Acme Corp", preview: "Re: Partnership inquiry", time: "5m ago", unread: false },
              { type: "chat", name: "Support Bot", preview: "New lead captured", time: "8m ago", unread: false },
            ].map((message, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 + i * 0.1 }}
                className={`
                  flex items-start gap-3 p-3 rounded-xl transition-colors cursor-pointer
                  ${message.unread ? 'bg-primary/5 border border-primary/10' : 'bg-dark-muted/5 border border-transparent hover:bg-dark-muted/10'}
                `}
              >
                <div className={`
                  w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0
                  ${message.type === "voice" ? "bg-blue-500/15" :
                    message.type === "sms" ? "bg-green-500/15" :
                    message.type === "email" ? "bg-amber-500/15" :
                    "bg-purple-500/15"}
                `}>
                  {message.type === "voice" && <Phone className="w-3.5 h-3.5 text-blue-400" />}
                  {message.type === "sms" && <MessageSquare className="w-3.5 h-3.5 text-green-400" />}
                  {message.type === "email" && <Inbox className="w-3.5 h-3.5 text-amber-400" />}
                  {message.type === "chat" && <MessageSquare className="w-3.5 h-3.5 text-purple-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-0.5">
                    <span className={`text-xs font-medium truncate ${message.unread ? 'text-dark-text' : 'text-dark-muted'}`}>
                      {message.name}
                    </span>
                    <span className="text-[10px] text-dark-muted flex-shrink-0">{message.time}</span>
                  </div>
                  <p className={`text-[11px] truncate ${message.unread ? 'text-dark-text/80' : 'text-dark-muted'}`}>
                    {message.preview}
                  </p>
                </div>
                {message.unread && (
                  <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-1" />
                )}
              </motion.div>
            ))}
          </div>

          {/* Footer status */}
          <div className="px-4 py-3 border-t border-dark-border bg-[hsl(216_28%_7%)] rounded-b-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="flex -space-x-1">
                  {["blue", "green", "amber", "purple", "pink"].map((color, i) => (
                    <div 
                      key={i}
                      className={`w-4 h-4 rounded-full border-2 border-[hsl(216_28%_7%)]
                        ${color === "blue" ? "bg-blue-500/40" :
                          color === "green" ? "bg-green-500/40" :
                          color === "amber" ? "bg-amber-500/40" :
                          color === "purple" ? "bg-purple-500/40" :
                          "bg-pink-500/40"}
                      `}
                    />
                  ))}
                </div>
                <span className="text-[10px] text-dark-muted">5 channels synced</span>
              </div>
              <motion.div
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="text-[10px] text-primary font-medium"
              >
                Live
              </motion.div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Integrations Visual (Dark Mode) - Copied from Agency page
const IntegrationsVisual = () => {
  const integrations = [
    { name: "Google Calendar", logo: googleCalendarLogo, category: "Calendar" },
    { name: "HubSpot", logo: hubspotLogo, category: "CRM" },
    { name: "Jobber", logo: jobberLogo, category: "Field Service" },
    { name: "OpenTable", logo: opentableLogo, category: "Reservations" },
    { name: "GoHighLevel", logo: ghlLogo, category: "Marketing" },
    { name: "Housecall Pro", logo: housecallProLogo, category: "Home Service" },
  ];
  
  return (
    <div className="relative w-full h-full flex items-center justify-center p-3 sm:p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-[420px] sm:max-w-[500px] md:max-w-[560px] bg-dark-surface rounded-2xl border border-dark-border shadow-2xl overflow-hidden origin-top scale-[0.9] sm:scale-[0.95] md:scale-100"
        style={{ boxShadow: '0 0 60px rgba(0,0,0,0.4)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-5 py-3.5 sm:py-4 border-b border-dark-border">
          <div className="flex items-center gap-2.5 sm:gap-3">
            <div className="w-8 sm:w-9 h-8 sm:h-9 rounded-lg bg-primary/15 flex items-center justify-center">
              <Plug className="w-4 sm:w-5 h-4 sm:h-5 text-primary" />
            </div>
            <div>
              <h3 className="text-[13px] sm:text-sm font-semibold text-dark-text">Connected Integrations</h3>
              <p className="text-[10px] text-dark-muted">All systems synced automatically</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 border border-primary/20">
            <div className="w-1.5 h-1.5 rounded-full bg-primary" />
            <span className="text-[9px] sm:text-[10px] font-medium text-primary">6 Active</span>
          </div>
        </div>

        {/* Integration Grid */}
        <div className="p-3.5 sm:p-4">
          <div className="grid grid-cols-2 gap-2 sm:gap-3">
            {integrations.map((integration, i) => (
              <motion.div
                key={integration.name}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 + i * 0.06, duration: 0.3 }}
                className="flex items-center gap-2 sm:gap-2.5 p-2 sm:p-2.5 rounded-xl bg-[hsl(216_28%_9%)] border border-dark-border/50 hover:border-dark-border transition-colors"
              >
                {/* Logo */}
                <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-white flex items-center justify-center overflow-hidden p-1.5 flex-shrink-0">
                  <Image 
                    src={integration.logo} 
                    alt={integration.name}
                    width={32}
                    height={32}
                    className="w-full h-full object-contain"
                  />
                </div>
                
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="text-[10.5px] sm:text-[11.5px] font-medium text-dark-text truncate">{integration.name}</div>
                  <div className="text-[9.5px] text-dark-muted">{integration.category}</div>
                </div>
                
                {/* Status */}
                <div className="w-4.5 h-4.5 sm:w-5 sm:h-5 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0">
                  <Check className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-primary" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-5 py-2.5 border-t border-dark-border bg-[hsl(216_28%_7%)]">
          <div className="flex items-center justify-between gap-3">
            <span className="text-[10px] sm:text-[11px] text-dark-muted">One-click setup • No code required</span>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 text-[10.5px] sm:text-[11px] font-medium text-dark-text bg-dark-muted/10 hover:bg-dark-muted/20 rounded-lg transition-colors"
            >
              <span>Browse All</span>
              <ChevronRight className="w-3 h-3" />
            </motion.button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Workflows Visual (Dark Mode) - Flowchart style matching Agency page
const WorkflowsVisual = () => {
  return (
    <div className="relative w-full h-full flex items-center justify-center overflow-hidden max-w-[520px] mx-auto">
      {/* Canvas background with subtle grid dots */}
      <div className="absolute inset-0 opacity-20">
        <div 
          className="w-full h-full"
          style={{
            backgroundImage: 'radial-gradient(circle, hsl(var(--dark-muted)) 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }}
        />
      </div>
      
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="relative flex flex-col items-center max-w-[460px] w-full scale-[0.96] sm:scale-100"
      >
        {/* SVG Connectors - Simple vertical lines like Agency page */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ overflow: 'visible' }}>
          {/* Node 1 to Node 2 */}
          <motion.line
            x1="50%" y1="84" x2="50%" y2="110"
            stroke="hsl(var(--dark-border))"
            strokeWidth="2"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ delay: 0.2, duration: 0.3 }}
          />
          {/* Node 2 to Node 3 */}
          <motion.line
            x1="50%" y1="154" x2="50%" y2="180"
            stroke="hsl(var(--dark-border))"
            strokeWidth="2"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ delay: 0.4, duration: 0.3 }}
          />
          {/* Node 3 to Node 4 */}
          <motion.line
            x1="50%" y1="224" x2="50%" y2="250"
            stroke="hsl(var(--dark-border))"
            strokeWidth="2"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ delay: 0.6, duration: 0.3 }}
          />
          {/* Node 4 to Node 5 */}
          <motion.line
            x1="50%" y1="294" x2="50%" y2="320"
            stroke="hsl(var(--dark-border))"
            strokeWidth="2"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ delay: 0.8, duration: 0.3 }}
          />
        </svg>

        {/* Node 1: Active AI Conversation (Featured) */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1, duration: 0.5 }}
          className="relative z-10 px-5 py-4 rounded-2xl bg-gradient-to-br from-dark-surface to-[hsl(210_16%_14%)] border border-dark-border shadow-2xl"
          style={{ boxShadow: '0 0 40px rgba(95, 143, 132, 0.1), 0 8px 32px rgba(0,0,0,0.4)' }}
        >
          <div className="flex items-center justify-between gap-6 mb-3">
            <span className="text-sm font-semibold text-dark-text">Active AI Conversation</span>
            <motion.div 
              animate={{ opacity: [1, 0.5, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-primary/15 border border-primary/25"
            >
              <div className="w-1.5 h-1.5 rounded-full bg-primary" />
              <span className="text-[10px] font-medium text-primary uppercase tracking-wide">Live</span>
            </motion.div>
          </div>
          {/* Audio Waveform */}
          <div className="flex items-center justify-center gap-0.5 h-8">
            {Array.from({ length: 32 }).map((_, i) => (
              <motion.div
                key={i}
                className="w-1 rounded-full bg-gradient-to-t from-blue-500 to-purple-500"
                animate={{
                  height: [8, 4 + Math.sin(i * 0.5) * 16 + Math.random() * 8, 8],
                }}
                transition={{
                  duration: 0.8 + Math.random() * 0.4,
                  repeat: Infinity,
                  repeatType: "reverse",
                  delay: i * 0.03,
                }}
              />
            ))}
          </div>
        </motion.div>

        {/* Connector dot */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.3 }}
          className="w-2 h-2 rounded-full bg-dark-border my-1 z-10"
        />

        {/* Node 2: AI Checks Calendar */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.4 }}
          className="relative z-10 flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-dark-surface border border-dark-border shadow-lg mt-2"
        >
          <div className="w-7 h-7 rounded-lg bg-blue-500/15 flex items-center justify-center">
            <Calendar className="w-4 h-4 text-blue-400" />
          </div>
          <span className="text-sm font-medium text-dark-text">AI Checks Calendar</span>
        </motion.div>

        {/* Connector dot */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.5 }}
          className="w-2 h-2 rounded-full bg-dark-border my-1 z-10"
        />

        {/* Node 3: Books Appointment */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.4 }}
          className="relative z-10 flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-dark-surface border border-dark-border shadow-lg mt-2"
        >
          <div className="w-7 h-7 rounded-lg bg-green-500/15 flex items-center justify-center">
            <Check className="w-4 h-4 text-green-400" />
          </div>
          <span className="text-sm font-medium text-dark-text">Books Appointment</span>
        </motion.div>

        {/* Connector dot */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.7 }}
          className="w-2 h-2 rounded-full bg-dark-border my-1 z-10"
        />

        {/* Node 4: Call Ends */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.4 }}
          className="relative z-10 flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-dark-surface border border-dark-border shadow-lg mt-2"
        >
          <div className="w-7 h-7 rounded-lg bg-purple-500/15 flex items-center justify-center">
            <Phone className="w-4 h-4 text-purple-400" />
          </div>
          <span className="text-sm font-medium text-dark-text">Call Ends</span>
        </motion.div>

        {/* Connector dot */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.9 }}
          className="w-2 h-2 rounded-full bg-dark-border my-1 z-10"
        />

        {/* Node 5: Sends Confirmation (Final) */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.9, duration: 0.4 }}
          className="relative z-10 flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-primary/15 border border-primary/30 shadow-lg mt-2"
          style={{ boxShadow: '0 0 16px rgba(95, 143, 132, 0.2)' }}
        >
          <div className="w-7 h-7 rounded-lg bg-amber-500/15 flex items-center justify-center">
            <MessageSquare className="w-4 h-4 text-amber-400" />
          </div>
          <span className="text-sm font-medium text-primary">Sends Confirmation</span>
          <Check className="w-4 h-4 text-primary" />
        </motion.div>
      </motion.div>
    </div>
  );
};

// Escalations Visual
const EscalationsVisual = () => {
  const [displayedText, setDisplayedText] = useState('');
  const fullText = "I need to speak with someone urgently about a leak";
  const indexRef = useRef(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    indexRef.current = 0;
    setDisplayedText('');
    
    const typeNextChar = () => {
      if (indexRef.current < fullText.length) {
        indexRef.current++;
        setDisplayedText(fullText.slice(0, indexRef.current));
        timeoutRef.current = setTimeout(typeNextChar, 50);
      } else {
        // Reset and loop after a pause
        timeoutRef.current = setTimeout(() => {
          indexRef.current = 0;
          setDisplayedText('');
          timeoutRef.current = setTimeout(typeNextChar, 400);
        }, 3000);
      }
    };
    
    // Start typing immediately
    typeNextChar();
    
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="w-full h-full flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-4">
        {/* AI Conversation with typing */}
        <div className="bg-dark-surface rounded-xl p-4 border border-dark-border">
          <p className="text-xs text-dark-muted mb-2">AI Conversation</p>
          <div className="space-y-2 text-sm">
            <div className="bg-dark-muted/20 rounded-lg p-2 min-h-[44px]">
              <p className="text-dark-text">
                "{displayedText}"
                {displayedText.length < fullText.length && (
                  <motion.span
                    animate={{ opacity: [1, 0] }}
                    transition={{ duration: 0.5, repeat: Infinity, ease: "easeInOut" }}
                    className="inline-block w-0.5 h-4 bg-primary ml-0.5 align-middle"
                  />
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Flagged as urgent - always visible */}
        <div className="bg-orange-500/20 rounded-xl p-4 border border-orange-500/30">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-orange-400" />
            <span className="text-sm font-medium text-orange-400">Flagged: Urgent Request</span>
          </div>
        </div>

        {/* Push notification - always visible */}
        <div className="bg-dark-surface rounded-xl p-4 border border-dark-border shadow-xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm font-medium text-dark-text">Ayra AI</p>
              <p className="text-xs text-dark-muted">Escalation: Urgent request from Sarah M.</p>
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <span className="px-3 py-1.5 bg-primary text-white text-xs font-medium rounded-lg">
              Tap to join call →
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Full Visibility Visual - Dashboard from Agency page
const FullVisibilityVisual = () => {
  return (
    <div className="relative w-full h-full flex items-center justify-center p-2">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 bg-dark-surface rounded-xl md:rounded-2xl border border-dark-border shadow-2xl overflow-hidden w-full max-w-[420px] sm:max-w-[480px] md:max-w-[640px]"
        style={{ boxShadow: "0 0 60px rgba(0,0,0,0.4)" }}
      >
        <div className="flex h-[360px] sm:h-[400px] md:h-[420px]">
          {/* Sidebar Navigation */}
          <div className="w-[110px] sm:w-[120px] md:w-[130px] bg-[hsl(216_28%_6%)] border-r border-dark-border flex flex-col py-2 px-1.5 md:py-2 md:px-1.5">
            {/* Logo */}
            <div className="flex items-center gap-1.5 px-2 mb-3 md:mb-3">
              <div className="w-5 h-5 md:w-5 md:h-5 rounded-md bg-primary flex items-center justify-center">
                <span className="text-[8px] md:text-[9px] font-bold text-white">
                  A
                </span>
              </div>
              <span className="text-[10px] md:text-[11px] font-semibold text-dark-text">
                Ayra
              </span>
            </div>

            {/* Nav Items */}
            <nav className="flex-1 space-y-0.5 overflow-y-auto">
              {[
                { icon: LayoutDashboard, label: "Dashboard", active: true },
                { icon: AlertTriangle, label: "Escalations", badge: 2 },
                { icon: History, label: "Call History" },
                { icon: Inbox, label: "Inbox", badge: 1 },
                { icon: BarChart3, label: "Analytics" },
                { icon: LifeBuoy, label: "Support" },
                { icon: Settings, label: "Settings" },
              ].map((item, i) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.02 }}
                  className={`flex items-center gap-1.5 px-2 py-1.5 md:py-1 rounded-md text-[9px] md:text-[10px] cursor-pointer transition-colors ${
                    item.active
                      ? "bg-dark-muted/15 text-dark-text"
                      : "text-dark-muted hover:text-dark-text hover:bg-dark-muted/10"
                  }`}
                >
                  <item.icon className="w-3 h-3 md:w-3 md:h-3 flex-shrink-0" />
                  <span className="flex-1 truncate">
                    {item.label}
                  </span>
                  {item.badge && (
                    <span className="w-3.5 h-3.5 md:w-3.5 md:h-3.5 rounded-full bg-primary text-[8px] md:text-[8px] font-medium text-white flex items-center justify-center">
                      {item.badge}
                    </span>
                  )}
                </motion.div>
              ))}
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1 flex flex-col overflow-hidden bg-[hsl(216_28%_8%)]">
            {/* Header */}
            <div className="flex items-center justify-between px-3 md:px-3 py-2.5 md:py-2 border-b border-dark-border">
              <div>
                <h4 className="text-[10px] md:text-[11px] font-semibold text-dark-text">
                  Welcome back, Trent
                </h4>
                <p className="text-[8px] md:text-[9px] text-dark-muted">
                  AI agent performance
                </p>
              </div>
              <div className="flex items-center gap-1.5 md:gap-1.5">
                <div className="w-5 h-5 md:w-5 md:h-5 rounded-full bg-dark-muted/20 flex items-center justify-center">
                  <span className="text-[8px] md:text-[9px] text-dark-muted">
                    🔔
                  </span>
                </div>
                <div className="w-5 h-5 md:w-5 md:h-5 rounded-full bg-primary/20 flex items-center justify-center">
                  <span className="text-[8px] md:text-[9px] font-medium text-primary">
                    T
                  </span>
                </div>
              </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto p-2.5 md:p-2.5 space-y-2 md:space-y-2">
              {/* Metric Cards */}
              <div className="grid grid-cols-4 gap-2 md:gap-1.5">
                {[
                  { icon: Phone, label: "Calls", value: "47", trend: "+32%", color: "blue" },
                  { icon: Calendar, label: "Booked", value: "19", trend: "+28%", color: "green" },
                  { icon: Star, label: "Rating", value: "4.9", color: "amber" },
                  { icon: MessageSquare, label: "Messages", value: "12", color: "purple" },
                ].map((metric, i) => (
                  <motion.div
                    key={metric.label}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 + i * 0.03 }}
                    className="bg-dark-surface rounded-lg md:rounded-lg p-2 md:p-2 border border-dark-border"
                  >
                    <div className="flex items-center gap-1.5 md:gap-1.5 mb-1 md:mb-1">
                      <div
                        className={`w-4 h-4 md:w-4 md:h-4 rounded flex items-center justify-center ${
                          metric.color === "blue"
                            ? "bg-blue-500/15"
                            : metric.color === "green"
                            ? "bg-green-500/15"
                            : metric.color === "amber"
                            ? "bg-amber-500/15"
                            : "bg-purple-500/15"
                        }`}
                      >
                        <metric.icon
                          className={`w-2.5 h-2.5 md:w-2.5 md:h-2.5 ${
                            metric.color === "blue"
                              ? "text-blue-400"
                              : metric.color === "green"
                              ? "text-green-400"
                              : metric.color === "amber"
                              ? "text-amber-400"
                              : "text-purple-400"
                          }`}
                        />
                      </div>
                      <span className="text-[8px] md:text-[8px] text-dark-muted">
                        {metric.label}
                      </span>
                    </div>
                    <div className="flex items-baseline gap-1 md:gap-1">
                      <span className="text-sm md:text-sm font-bold text-dark-text">
                        {metric.value}
                      </span>
                      {metric.trend && (
                        <span className="text-[8px] md:text-[8px] text-primary">
                          {metric.trend}
                        </span>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-1.5 md:gap-1.5">
                {[
                  { icon: Headphones, label: "Listen" },
                  { icon: AlertTriangle, label: "Report" },
                  { icon: LifeBuoy, label: "Support" },
                  { icon: FileText, label: "Reports" },
                ].map((action, i) => (
                  <motion.button
                    key={action.label}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25 + i * 0.02 }}
                    className="flex-1 flex items-center justify-center gap-1 md:gap-1 py-1.5 md:py-1.5 rounded-lg md:rounded-lg bg-dark-surface border border-dark-border hover:border-dark-muted/50 transition-colors"
                  >
                    <action.icon className="w-3 h-3 md:w-3 md:h-3 text-dark-muted" />
                    <span className="text-[8px] md:text-[8px] text-dark-muted">
                      {action.label}
                    </span>
                  </motion.button>
                ))}
              </div>

              {/* Charts Row */}
              <div className="grid grid-cols-5 gap-2 md:gap-1.5">
                {/* Call Volume Chart */}
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="col-span-3 bg-dark-surface rounded-lg md:rounded-lg p-2 md:p-2 border border-dark-border"
                >
                  <h5 className="text-[9px] md:text-[9px] font-medium text-dark-text mb-1.5 md:mb-1.5">
                    Call Volume
                  </h5>
                  <div className="flex items-end gap-0.5 h-10 md:h-10">
                    {[18, 22, 16, 28, 24, 20, 14].map((h, i) => (
                      <motion.div
                        key={i}
                        initial={{ height: 0 }}
                        animate={{ height: `${h * 1.3}px` }}
                        transition={{ delay: 0.35 + i * 0.03, duration: 0.3 }}
                        className="flex-1 bg-gradient-to-t from-primary/60 to-primary rounded-sm"
                      />
                    ))}
                  </div>
                  <div className="flex justify-between mt-1 md:mt-1">
                    {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
                      <span
                        key={i}
                        className="text-[7px] md:text-[7px] text-dark-muted flex-1 text-center"
                      >
                        {d}
                      </span>
                    ))}
                  </div>
                </motion.div>

                {/* Call Outcomes */}
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.35 }}
                  className="col-span-2 bg-dark-surface rounded-lg md:rounded-lg p-2 md:p-2 border border-dark-border"
                >
                  <h5 className="text-[9px] md:text-[9px] font-medium text-dark-text mb-1">
                    Outcomes
                  </h5>
                  <div className="flex items-center gap-1.5 md:gap-1.5">
                    <div className="relative w-8 h-8 md:w-8 md:h-8">
                      <svg
                        className="w-8 h-8 md:w-8 md:h-8 -rotate-90"
                        viewBox="0 0 36 36"
                      >
                        <circle
                          cx="18"
                          cy="18"
                          r="14"
                          fill="none"
                          stroke="hsl(var(--dark-border))"
                          strokeWidth="4"
                        />
                        <motion.circle
                          cx="18"
                          cy="18"
                          r="14"
                          fill="none"
                          stroke="hsl(var(--primary))"
                          strokeWidth="4"
                          strokeDasharray="88"
                          initial={{ strokeDashoffset: 88 }}
                          animate={{ strokeDashoffset: 88 - 88 * 0.52 }}
                          transition={{ delay: 0.4, duration: 0.4 }}
                        />
                      </svg>
                    </div>
                    <div className="space-y-0.5 text-[7px] md:text-[7px]">
                      <div className="flex items-center gap-1 md:gap-1">
                        <div className="w-1 h-1 rounded-full bg-primary" />
                        <span className="text-dark-muted">Booked 52%</span>
                      </div>
                      <div className="flex items-center gap-1 md:gap-1">
                        <div className="w-1 h-1 rounded-full bg-blue-500" />
                        <span className="text-dark-muted">Info 28%</span>
                      </div>
                      <div className="flex items-center gap-1 md:gap-1">
                        <div className="w-1 h-1 rounded-full bg-dark-muted/40" />
                        <span className="text-dark-muted">Other 20%</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Recent Calls */}
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-dark-surface rounded-lg md:rounded-lg p-2.5 md:p-2.5 border border-dark-border"
              >
                <div className="flex items-center justify-between mb-2 md:mb-2">
                  <h5 className="text-[10px] md:text-[10px] font-medium text-dark-text">
                    Recent Calls
                  </h5>
                  <div className="flex gap-0.5">
                    {["Calls", "Transcripts", "Recordings"].map((tab, i) => (
                      <span
                        key={tab}
                        className={`text-[7px] md:text-[7px] px-1.5 md:px-1.5 py-0.5 rounded ${
                          i === 0
                            ? "bg-primary/20 text-primary"
                            : "text-dark-muted hover:text-dark-text cursor-pointer"
                        }`}
                      >
                        {tab}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="space-y-0">
                  {[
                    { time: "3:15 PM", name: "Mike Thompson", outcome: "Booked", duration: "5:12", color: "green" },
                    { time: "2:48 PM", name: "Sarah Chen", outcome: "Info Provided", duration: "2:45", color: "blue" },
                    { time: "2:20 PM", name: "James Wilson", outcome: "Booked", duration: "4:18", color: "green" },
                    { time: "1:55 PM", name: "Lisa Rodriguez", outcome: "Transferred", duration: "1:32", color: "amber" },
                    { time: "1:30 PM", name: "David Kim", outcome: "Booked", duration: "3:55", color: "green" },
                  ].map((call, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.45 + i * 0.03 }}
                      className="flex items-center gap-2 md:gap-2 py-1.5 md:py-1.5 border-b border-dark-border/30 last:border-0"
                    >
                      <span className="text-[8px] md:text-[8px] text-dark-muted w-10 md:w-10">
                        {call.time}
                      </span>
                      <span className="text-[9px] md:text-[9px] font-medium text-dark-text flex-1 truncate">
                        {call.name}
                      </span>
                      <span
                        className={`text-[8px] md:text-[8px] w-16 md:w-16 truncate ${
                          call.color === "green"
                            ? "text-primary"
                            : call.color === "blue"
                            ? "text-blue-400"
                            : call.color === "amber"
                            ? "text-amber-400"
                            : "text-dark-muted"
                        }`}
                      >
                        {call.outcome}
                      </span>
                      <span className="text-[8px] md:text-[8px] text-dark-muted w-7 md:w-7 text-right">
                        {call.duration}
                      </span>
                      <button className="w-4 h-4 md:w-4 md:h-4 rounded-full bg-dark-muted/10 hover:bg-primary/20 flex items-center justify-center transition-colors group">
                        <Play className="w-2 h-2 md:w-2 md:h-2 text-dark-muted group-hover:text-primary" />
                      </button>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const FeatureVisual = ({ featureId }: { featureId: string }) => {
  if (featureId === "visibility") {
    return (
      <div className="max-w-[440px] sm:max-w-[480px] mx-auto scale-[0.96] sm:scale-[0.99] lg:scale-100">
        <FullVisibilityVisual />
      </div>
    );
  }

  switch (featureId) {
    case "voice":
      return <VoiceAIVisual />;
    case "omnichannel":
      return <OmnichannelVisual />;
    case "integrations":
      return <IntegrationsVisual />;
    case "workflows":
      return <WorkflowsVisual />;
    case "escalations":
      return <EscalationsVisual />;
    default:
      return null;
  }
};

const BusinessDarkScrollSection = () => {
  const stickyContainerRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  
  const { scrollYProgress } = useScroll({
    target: stickyContainerRef,
    offset: ["start start", "end end"],
  });

  useEffect(() => {
    const unsubscribe = scrollYProgress.on("change", (latest) => {
      const index = Math.min(
        Math.floor(latest * features.length),
        features.length - 1
      );
      setActiveIndex(index);
    });
    return () => unsubscribe();
  }, [scrollYProgress]);

  return (
    <section className="relative bg-black">
      {/* PART A: Intro */}
      <div className="min-h-[60vh] md:min-h-screen flex items-center justify-center px-4 md:px-6 py-12 md:py-0">
        <div className="text-center max-w-4xl">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl text-dark-text headline-display px-2"
          >
            Everything you need to never miss another <span className="ayra-accent">customer</span>.
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
            className="mt-4 md:mt-6 text-base md:text-lg text-dark-muted body-text px-2"
          >
            Ayra handles it all while you focus on your business.
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            viewport={{ once: true }}
            className="mt-8 md:mt-12 text-dark-muted animate-bounce text-sm md:text-base"
          >
            ↓ Scroll to explore
          </motion.div>
        </div>
      </div>

      {/* PART B: Mobile stacked feature cards */}
      <div className="lg:hidden px-4 pb-16 space-y-8">
        {features.map((feature, idx) => {
          const Icon = feature.icon;
          return (
            <motion.div
              key={feature.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: idx * 0.05 }}
              viewport={{ once: true, margin: "-40px" }}
              className="rounded-2xl p-1.5 sm:p-2"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-dark-surface flex items-center justify-center border border-dark-border/60 text-primary">
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg sm:text-xl font-semibold text-dark-text whitespace-pre-line leading-tight">
                    {feature.title}
                  </h3>
                  <p className="mt-2 text-sm text-dark-muted whitespace-pre-line leading-relaxed">
                    {feature.subtitle}
                  </p>
                </div>
              </div>

              <div className="mt-4 rounded-xl bg-transparent p-0">
                <div className="min-h-[300px] sm:min-h-[360px] flex items-center justify-center overflow-hidden rounded-xl">
                  <div className="w-full max-w-[520px] scale-[0.98] sm:scale-100">
                    <FeatureVisual featureId={feature.id} />
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* PART B: Sticky Scroll */}
      <div 
        ref={stickyContainerRef} 
        className="relative hidden lg:block"
        style={{ height: `${features.length * 100}vh` }}
      >
        <div className="sticky top-0 h-screen flex items-center">
          <div className="container mx-auto max-w-7xl px-4 md:px-6">
            <div className="grid lg:grid-cols-2 gap-6 md:gap-12 items-center">
              {/* Left: Text content - prerender all, fade between */}
              <div className="relative min-h-[280px] md:min-h-[320px]">
                {/* Text content with crossfade */}
                <div className="relative">
                  {features.map((feature, i) => (
                    <motion.div
                      key={feature.id}
                      initial={false}
                      animate={{ 
                        opacity: i === activeIndex ? 1 : 0,
                      }}
                      transition={{ duration: 0.35, ease: "easeOut" }}
                      className={i === 0 ? "relative" : "absolute inset-0"}
                      style={{ 
                        pointerEvents: i === activeIndex ? 'auto' : 'none',
                        willChange: 'opacity',
                      }}
                    >
                      <h3 className="text-xl sm:text-2xl md:text-4xl lg:text-5xl text-dark-text headline-display whitespace-pre-line">
                        {feature.title}
                      </h3>
                      <p className="mt-3 md:mt-4 text-sm md:text-lg text-dark-muted body-text whitespace-pre-line">
                        {feature.subtitle}
                      </p>
                    </motion.div>
                  ))}
                </div>

                {/* Progress list */}
                <div className="space-y-2 md:space-y-3 pt-4 md:pt-6 mt-6 border-t border-dark-border">
                  {features.map((feature, i) => {
                    const Icon = feature.icon;
                    return (
                      <motion.div
                        key={feature.id}
                        initial={false}
                        animate={{
                          opacity: i === activeIndex ? 1 : 0.4,
                        }}
                        transition={{ duration: 0.25 }}
                        className="flex items-center gap-2 md:gap-3"
                      >
                        <div
                          className={`w-6 h-6 md:w-8 md:h-8 rounded-lg flex items-center justify-center transition-colors duration-200 ${
                            i === activeIndex
                              ? "bg-primary text-white"
                              : "bg-dark-surface text-dark-muted"
                          }`}
                        >
                          <Icon className="w-3 h-3 md:w-4 md:h-4" />
                        </div>
                        <span
                          className={`text-xs md:text-sm transition-colors duration-200 ${
                            i === activeIndex
                              ? "text-primary font-medium"
                              : "text-dark-muted"
                          }`}
                        >
                          {feature.id === "voice" ? "Voice AI" : 
                           feature.id === "omnichannel" ? "Omnichannel" :
                           feature.id === "integrations" ? "Integrations" :
                           feature.id === "workflows" ? "Workflows" :
                           feature.id === "escalations" ? "Escalations" :
                           "Full Visibility"}
                        </span>
                      </motion.div>
                    );
                  })}
                </div>
              </div>

              {/* Right: Visual - All preloaded, GPU-accelerated crossfade */}
              <div 
                className="relative h-[300px] sm:h-[350px] md:h-[450px] lg:h-[500px]"
                style={{ 
                  contain: 'layout style',
                }}
              >
                {/* Render all visuals, control visibility with opacity only */}
                {features.map((feature, i) => (
                  <motion.div
                    key={feature.id}
                    initial={false}
                    animate={{ 
                      opacity: i === activeIndex ? 1 : 0,
                    }}
                    transition={{ 
                      duration: 0.4, 
                      ease: [0.4, 0, 0.2, 1],
                    }}
                    className="absolute inset-0"
                    style={{ 
                      willChange: 'opacity',
                      backfaceVisibility: 'hidden',
                      transform: 'translateZ(0)',
                      pointerEvents: i === activeIndex ? 'auto' : 'none',
                      zIndex: i === activeIndex ? 1 : 0,
                    }}
                  >
                    <FeatureVisual featureId={feature.id} />
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BusinessDarkScrollSection;
